/**
 * Task Status Icon
 *
 * Shared status icon language for release-facing Spaces task surfaces.
 *
 * Primary responsibilities:
 * - Resolve status aliases into the board icon set
 * - Render consistent status icons across task UIs
 *
 * Dependencies:
 * - @tabler/icons-react
 */

import {
  IconCircleDashed,
  IconCircleDashedMinus,
  IconCircleFilled,
  IconLoader,
  IconProgressAlert,
  IconX
} from "@tabler/icons-react"
import { cn } from "@/lib/utils"

export type TaskStatusIconName =
  | "todo"
  | "in_progress"
  | "done"
  | "blocked"
  | "cancelled"
  | "need_review"

type TaskStatusIconInput = {
  status?: string | null
  title?: string | null
  statuses?: string[] | null
}

const STATUS_ICON_ALIASES: Record<TaskStatusIconName, string[]> = {
  todo: ["todo", "to_do", "pending", "backlog", "new"],
  in_progress: [
    "in_progress",
    "inprogress",
    "doing",
    "active",
    "started",
    "working"
  ],
  done: ["done", "completed", "finished", "closed"],
  blocked: ["blocked", "stuck", "on_hold", "hold"],
  cancelled: ["cancelled", "canceled", "aborted"],
  need_review: [
    "need_review",
    "needs_review",
    "review",
    "in_review",
    "awaiting_review"
  ]
}

const normalizeStatusCandidate = (value: string | null | undefined) => {
  if (!value) return ""
  return value.toLowerCase().replace(/[-\s]/g, "_")
}

const hasAliasMatch = (
  iconName: TaskStatusIconName,
  candidateValues: string[]
) => candidateValues.some(value => STATUS_ICON_ALIASES[iconName].includes(value))

export const resolveTaskStatusIconName = ({
  status,
  title,
  statuses
}: TaskStatusIconInput): TaskStatusIconName => {
  const candidateValues = [
    status,
    title,
    ...(statuses || [])
  ]
    .filter(Boolean)
    .map(value => normalizeStatusCandidate(value))

  if (hasAliasMatch("need_review", candidateValues)) return "need_review"
  if (hasAliasMatch("blocked", candidateValues)) return "blocked"
  if (hasAliasMatch("cancelled", candidateValues)) return "cancelled"
  if (hasAliasMatch("done", candidateValues)) return "done"
  if (hasAliasMatch("in_progress", candidateValues)) return "in_progress"
  return "todo"
}

export const getTaskStatusIconToneClass = (
  iconName: TaskStatusIconName
) => {
  switch (iconName) {
    case "in_progress":
      return "text-blue-500 dark:text-blue-400"
    case "done":
      return "text-emerald-500 dark:text-emerald-400"
    case "blocked":
      return "text-red-500 dark:text-red-400"
    case "cancelled":
      return "text-zinc-500 dark:text-zinc-400"
    case "need_review":
      return "text-amber-500 dark:text-amber-400"
    case "todo":
    default:
      return "text-slate-500 dark:text-slate-400"
  }
}

interface TaskStatusIconProps extends TaskStatusIconInput {
  iconName?: TaskStatusIconName
  className?: string
  size?: number
  stroke?: number
  testId?: string
}

export const TaskStatusIcon = ({
  iconName,
  status,
  title,
  statuses,
  className,
  size = 13,
  stroke = 1.8,
  testId
}: TaskStatusIconProps) => {
  const resolvedIconName =
    iconName ||
    resolveTaskStatusIconName({
      status,
      title,
      statuses
    })

  const wrapperClassName = cn(
    "flex size-4 shrink-0 items-center justify-center",
    className
  )

  if (resolvedIconName === "in_progress") {
    return (
      <span data-testid={testId} className={wrapperClassName} aria-hidden="true">
        <IconLoader size={size} stroke={stroke} />
      </span>
    )
  }

  if (resolvedIconName === "done") {
    return (
      <span data-testid={testId} className={wrapperClassName} aria-hidden="true">
        <IconCircleFilled size={Math.max(8, size - 3)} />
      </span>
    )
  }

  if (resolvedIconName === "blocked") {
    return (
      <span data-testid={testId} className={wrapperClassName} aria-hidden="true">
        <IconCircleDashedMinus size={size} stroke={stroke} />
      </span>
    )
  }

  if (resolvedIconName === "cancelled") {
    return (
      <span data-testid={testId} className={wrapperClassName} aria-hidden="true">
        <IconX size={size} stroke={stroke} />
      </span>
    )
  }

  if (resolvedIconName === "need_review") {
    return (
      <span data-testid={testId} className={wrapperClassName} aria-hidden="true">
        <IconProgressAlert size={size} stroke={stroke} />
      </span>
    )
  }

  return (
    <span data-testid={testId} className={wrapperClassName} aria-hidden="true">
      <IconCircleDashed size={size} stroke={stroke} />
    </span>
  )
}
